/**
 * What the sky and the ground do to everyone on the map (02 §5-§6.3, 04 §7): the storm, and the
 * snow that swallows sound. Each answer lives here once, because more than one layer asks for it -
 * the roguelike pays Cold Blooded, Desert Rat and Woodsman for cancelling the storm's drain exactly,
 * and the HUD draws the noise ring at the radius the garrison actually hears. Two hand-kept copies of
 * a number are how the game comes to tell a player one thing and do another.
 *
 * ROUND5 §3.2: the Storm modifier promised "vision -50 % both sides, stamina drain +20 %" and
 * nothing in `src/sim` read `def.weather` or `def.modifiers` for it at all. Pure.
 */

import { NOISE, secs, STORM } from './tuning';
import type { Biome, MissionDef, MissionParams } from './types';

/** All either side needs to know about the sky: what it is doing, and whether it was ordered. */
export type Sky = Pick<MissionDef, 'weather' | 'modifiers'> | Pick<MissionParams, 'weather' | 'modifiers'>;

/**
 * A whiteout, a sandstorm or a thunderstorm - each of them its biome's own storm weather, and each
 * blowing just as hard when it is chosen in Practice as when the card ordered it.
 *
 * The modifier stays in the test as a guarantee, not as the authority it used to be. Until
 * `thunderstorm` existed it WAS the authority: temperate's storm was plain `rain`, which a calm day
 * also brings, so nothing but the card could tell the two apart. Now every biome's storm weather is
 * one this function already calls a storm - tests/unit/thunderstorm.test.ts pins that against
 * `WEATHER` - and the clause only covers the missions no generator built: a hand-written map, the
 * `?quick=&modifiers=storm` dev route, a staged sim test. The card promises an effect; a mission
 * carrying it must never be worth nothing.
 */
export function inStorm(sky: Sky): boolean {
  return sky.weather === 'blizzard' || sky.weather === 'sandstorm' || sky.weather === 'thunderstorm'
    || sky.modifiers.includes('storm');
}

/**
 * A stroke of lightning, 0 to 1 (02 §5). The THUNDERSTORM throws it - not the biome, which was the
 * only handle there was before temperate had a storm weather of its own. A whiteout and a brown-out
 * still throw none. Derived from the tick and the mission's own seed rather than held in the world,
 * so it costs nothing to save and is replay-exact by construction - there is no state to get out of
 * step, and the schedule is the same one it always was.
 *
 * The intervals are irregular on purpose. A stroke lifts the storm's blindness for everyone at
 * once, and a player who could count the seconds between them would simply walk in the gaps. Pure.
 */
export function lightning(sky: Sky, seed: number, tick: number): number {
  if (sky.weather !== 'thunderstorm') return 0;
  const [near, far] = STORM.strikeEverySeconds;
  const span = secs(far - near);
  // Walk the schedule from the start of the mission: each gap is drawn from the seed, so the whole
  // sequence is fixed for a given mission and the same on every machine that plays it.
  let at = 0;
  let salt = (seed ^ 0x5f37) >>> 0;
  for (let i = 0; i < 4096 && at <= tick + STORM.strikeTicks; i++) {
    salt = (Math.imul(salt, 1664525) + 1013904223) >>> 0;
    at += secs(near) + (salt % (span + 1));
    const off = Math.abs(tick - at);
    if (off <= STORM.strikeTicks) return 1 - off / STORM.strikeTicks;
  }
  return 0;
}

/**
 * Multiplier on a soldier's sighting range: half of it, and nobody's eyes are spared - except for
 * the instant a stroke lights the whole map, when the storm lends no cover at all.
 */
export function stormVision(sky: Sky, seed = 0, tick = 0): number {
  if (!inStorm(sky)) return 1;
  return STORM.vision + (1 - STORM.vision) * lightning(sky, seed, tick);
}

/** Multiplier on how fast the ranger's fatigue bar fills, so a bigger stamina pool cancels it exactly. */
export function stormFatigue(sky: Sky): number {
  return inStorm(sky) ? 1 + STORM.fatigue : 1;
}

/**
 * Multiplier on the radius of any sound made on this ground: deep snow swallows a third of it
 * (`makeNoise`, alert.ts). The HUD reads it too - its ring is meant to be the distance at which a
 * soldier really hears him, and on snow it was drawing every footstep 30 % too wide (ROUND5 §3).
 */
export function muffle(biome: Biome): number {
  return biome === 'arctic' ? NOISE.arctic : 1;
}
